import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { bookingApi } from '@/api';
import { BookingPageProfile } from '@/types';

// Public, unauthenticated booking page at /book/:slug. Embedded as an
// iframe in inspector websites; also reachable as a direct URL. Mobile-first
// — most clients will book on their phone after clicking a link from the
// inspector's site or signature.
//
// Flow: load profile + slots → pick a date → pick a slot → fill the form →
// submit → confirmation screen with appointment details.

const DEFAULT_BRAND = '#2F6A44';

const sanitizeHexColor = (raw: string | null | undefined): string => {
  if (!raw) return DEFAULT_BRAND;
  const trimmed = raw.trim();
  return /^#[0-9a-fA-F]{6}$/.test(trimmed) ? trimmed : DEFAULT_BRAND;
};

const formatDateLabel = (ymd: string): string => {
  const [y, m, d] = ymd.split('-').map(Number);
  // Format in browser local TZ — date-only labels are unambiguous since
  // we only render the YYYY-MM-DD already determined in the inspector's TZ.
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
};

const formatTimeLabel = (hm: string): string => {
  const [h, m] = hm.split(':').map(Number);
  // Reconstruct as a local-time Date just to use the formatter; the actual
  // hour/minute values come straight from the inspector-TZ slot string, so
  // there's no TZ math here.
  const dt = new Date(2000, 0, 1, h, m);
  return dt.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// Convert a slot's wall-clock (in the inspector's TZ) into a real UTC
// instant. Same approach as the backend's zonedToUtc but lives here so
// the frontend can submit an unambiguous ISO string regardless of the
// browser's own TZ.
const zonedSlotToUtc = (ymd: string, hm: string, timezone: string): Date => {
  const [y, m, d] = ymd.split('-').map(Number);
  const [h, mi] = hm.split(':').map(Number);
  const naive = new Date(Date.UTC(y, m - 1, d, h, mi));
  // Read the offset of `timezone` at this instant from a formatter.
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'shortOffset',
  });
  const tzn = fmt.formatToParts(naive).find((p) => p.type === 'timeZoneName')?.value ?? '';
  if (tzn === 'GMT' || tzn === 'UTC') return naive;
  const match = tzn.match(/^GMT([+-])(\d{1,2})(?::?(\d{2}))?$/);
  if (!match) return naive;
  const sign = match[1] === '-' ? -1 : 1;
  const offsetMin = sign * (Number(match[2]) * 60 + Number(match[3] ?? 0));
  return new Date(naive.getTime() - offsetMin * 60_000);
};

// Friendly label for an IANA TZ — falls back to the raw name if Intl
// doesn't know a `long` form.
const tzShortLabel = (timezone: string): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? timezone;
  } catch {
    return timezone;
  }
};

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_LABELS = ['Su','Mo','Tu','We','Th','Fr','Sa'];

// Returns null-padded grid of day numbers for a month (nulls = blank cells before day 1).
const buildMonthGrid = (year: number, month: number): (number | null)[] => {
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const grid: (number | null)[] = Array(firstDow).fill(null);
  for (let d = 1; d <= daysInMonth; d++) grid.push(d);
  return grid;
};

const toYmd = (year: number, month: number, day: number): string =>
  `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const BookingPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [profile, setProfile] = useState<BookingPageProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [propertyAddress, setPropertyAddress] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<{
    scheduledDate: string;
    durationMinutes: number;
  } | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await bookingApi.getProfile(slug);
        if (cancelled) return;
        if (resp.success && resp.data) {
          setProfile(resp.data);
          // Auto-select the first date that has any slots so the user
          // doesn't have to scan the list looking for a non-empty day.
          const firstWithSlots = resp.data.availableSlots.find((d) => d.slots.length > 0);
          if (firstWithSlots) setSelectedDate(firstWithSlots.date);
        } else {
          setLoadError('This booking page is not available.');
        }
      } catch (err) {
        if (cancelled) return;
        const status =
          typeof err === 'object' && err !== null && 'response' in err
            ? (err as { response?: { status?: number } }).response?.status
            : null;
        setLoadError(
          status === 404
            ? 'This booking page is not available.'
            : 'Could not load the booking page. Please try again.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const datesWithSlots = useMemo(
    () => profile?.availableSlots.filter((d) => d.slots.length > 0) ?? [],
    [profile]
  );

  const availableDateSet = useMemo(
    () => new Set(datesWithSlots.map((d) => d.date)),
    [datesWithSlots]
  );

  // Calendar month — start on the month containing the first available date.
  const [currentMonth, setCurrentMonth] = useState<{ year: number; month: number }>(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  // When slots load, jump to the month of the first available date.
  useEffect(() => {
    if (datesWithSlots.length === 0) return;
    const [y, m] = datesWithSlots[0].date.split('-').map(Number);
    setCurrentMonth({ year: y, month: m - 1 });
  }, [datesWithSlots.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const prevMonth = useCallback(() => setCurrentMonth(({ year, month }) =>
    month === 0 ? { year: year - 1, month: 11 } : { year, month: month - 1 }
  ), []);

  const nextMonth = useCallback(() => setCurrentMonth(({ year, month }) =>
    month === 11 ? { year: year + 1, month: 0 } : { year, month: month + 1 }
  ), []);

  const calendarGrid = useMemo(
    () => buildMonthGrid(currentMonth.year, currentMonth.month),
    [currentMonth]
  );

  const today = useMemo(() => {
    const n = new Date();
    return toYmd(n.getFullYear(), n.getMonth(), n.getDate());
  }, []);

  const slotsForSelectedDate = useMemo(() => {
    if (!profile || !selectedDate) return [];
    return profile.availableSlots.find((d) => d.date === selectedDate)?.slots ?? [];
  }, [profile, selectedDate]);

  const canSubmit =
    !!selectedDate &&
    !!selectedSlot &&
    !!clientName.trim() &&
    !!clientEmail.trim() &&
    !!propertyAddress.trim() &&
    !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug || !selectedDate || !selectedSlot) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      // Convert the slot pick (which is wall-clock in the *inspector's*
      // TZ) into the canonical UTC instant. A buyer in EST picking the
      // "9:00 AM" slot for a PT inspector should land on PT 9:00 = ET
      // 12:00, regardless of the browser's TZ. Pre-this fix, we used
      // `new Date(y,m,d,h,mi)` which interpreted in the *buyer's* TZ.
      const scheduled = profile
        ? zonedSlotToUtc(selectedDate, selectedSlot, profile.timezone)
        : new Date(selectedDate + 'T' + selectedSlot);

      const resp = await bookingApi.create(slug, {
        clientName: clientName.trim(),
        clientEmail: clientEmail.trim(),
        clientPhone: clientPhone.trim() || undefined,
        propertyAddress: propertyAddress.trim(),
        scheduledDate: scheduled.toISOString(),
      });
      if (resp.success && resp.data) {
        setConfirmation({
          scheduledDate: resp.data.appointment.scheduledDate,
          durationMinutes: resp.data.appointment.durationMinutes,
        });
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } catch (err) {
      const backendMessage =
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response?: { data?: { error?: unknown } } }).response?.data?.error === 'string'
          ? ((err as { response: { data: { error: string } } }).response.data.error)
          : null;
      setSubmitError(backendMessage || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-slate-500 text-sm">Loading…</div>
      </div>
    );
  }

  if (loadError || !profile) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
          <h1 className="text-lg font-semibold text-slate-900 mb-2">Page not found</h1>
          <p className="text-sm text-slate-600">
            {loadError || 'This booking page is not available.'}
          </p>
        </div>
      </div>
    );
  }

  const brand = sanitizeHexColor(profile.brandColor);

  if (confirmation) {
    // Render in the inspector's TZ regardless of where the client is —
    // ambiguity here is what causes "wait, was that 9 AM your time or
    // mine?" miscommunication. The TZ short label (e.g. "PDT") makes
    // the frame explicit.
    const scheduledLabel = new Date(confirmation.scheduledDate).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: profile.timezone,
      timeZoneName: 'short',
    });
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-12">
        <div className="max-w-lg mx-auto bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white"
              style={{ backgroundColor: brand }}
            >
              ✓
            </div>
            <h1 className="text-xl font-semibold text-slate-900">Booking confirmed</h1>
          </div>
          <p className="text-sm text-slate-700">
            Your inspection at <strong>{propertyAddress}</strong> is scheduled for{' '}
            <strong>{scheduledLabel}</strong>.
          </p>
          <p className="text-sm text-slate-500 mt-3">
            A confirmation email has been sent to {clientEmail}. {profile.inspectorName} will reach out if anything changes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 sm:py-12">
      <div className="max-w-2xl mx-auto">
        {/* Inspector header */}
        <div className="mb-6">
          {profile.logoUrl && (
            <img
              src={profile.logoUrl}
              alt={profile.companyName}
              className="max-h-12 mb-3"
            />
          )}
          <h1 className="text-2xl font-semibold text-slate-900">
            Book an Inspection
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            with {profile.inspectorName} · {profile.companyName}
            {profile.phone ? ` · ${profile.phone}` : ''}
          </p>
        </div>

        {/* Date + slot picker */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sm:p-8 mb-6">
          {datesWithSlots.length === 0 ? (
            <p className="text-sm text-slate-600">
              No available times in the next 60 days. Please contact{' '}
              {profile.inspectorName} directly.
            </p>
          ) : (
            <>
              <label className="block text-sm font-medium text-slate-900 mb-3">
                Select a date
              </label>

              {/* Calendar */}
              <div className="select-none">
                {/* Month nav */}
                <div className="flex items-center justify-between mb-3">
                  <button
                    type="button"
                    onClick={prevMonth}
                    className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors"
                    aria-label="Previous month"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                  </button>
                  <span className="text-sm font-semibold text-slate-800">
                    {MONTH_NAMES[currentMonth.month]} {currentMonth.year}
                  </span>
                  <button
                    type="button"
                    onClick={nextMonth}
                    className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors"
                    aria-label="Next month"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                  </button>
                </div>

                {/* Day-of-week headers */}
                <div className="grid grid-cols-7 mb-1">
                  {DAY_LABELS.map((d) => (
                    <div key={d} className="text-center text-xs font-medium text-slate-400 py-1">{d}</div>
                  ))}
                </div>

                {/* Day cells */}
                <div className="grid grid-cols-7 gap-y-1">
                  {calendarGrid.map((day, i) => {
                    if (day === null) return <div key={`blank-${i}`} />;
                    const ymd = toYmd(currentMonth.year, currentMonth.month, day);
                    const isAvailable = availableDateSet.has(ymd);
                    const isSelected = selectedDate === ymd;
                    const isPast = ymd < today;
                    return (
                      <button
                        key={ymd}
                        type="button"
                        disabled={!isAvailable || isPast}
                        onClick={() => { setSelectedDate(ymd); setSelectedSlot(null); }}
                        className={[
                          'mx-auto flex items-center justify-center w-9 h-9 rounded-full text-sm transition-colors',
                          isSelected
                            ? 'text-white font-semibold'
                            : isAvailable && !isPast
                              ? 'hover:opacity-80 font-medium'
                              : 'text-slate-300 cursor-default',
                        ].join(' ')}
                        style={
                          isSelected
                            ? { backgroundColor: brand }
                            : isAvailable && !isPast
                              ? { color: brand, border: `1.5px solid ${brand}` }
                              : undefined
                        }
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>

                {selectedDate && (
                  <p className="mt-3 text-sm text-slate-600 text-center">
                    {formatDateLabel(selectedDate)}
                  </p>
                )}
              </div>

              {slotsForSelectedDate.length > 0 && (
                <>
                  <div className="flex items-baseline justify-between mt-5 mb-2 gap-2 flex-wrap">
                    <label className="block text-sm font-medium text-slate-900">
                      Available times ({profile.durationMinutes} minutes)
                    </label>
                    <span className="text-xs text-slate-500">
                      Times shown in {tzShortLabel(profile.timezone)} ({profile.timezone})
                    </span>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {slotsForSelectedDate.map((s) => {
                      const active = selectedSlot === s;
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setSelectedSlot(s)}
                          className="px-3 py-2 rounded-md text-sm font-medium border transition-colors"
                          style={{
                            backgroundColor: active ? brand : '#ffffff',
                            color: active ? '#ffffff' : '#0f172a',
                            borderColor: active ? brand : '#cbd5e1',
                          }}
                        >
                          {formatTimeLabel(s)}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Form (only meaningful if a slot is picked) */}
        {selectedSlot && (
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sm:p-8"
          >
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Your details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Full name
                </label>
                <input
                  type="text"
                  required
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Phone <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  type="tel"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Property address
                </label>
                <input
                  type="text"
                  required
                  value={propertyAddress}
                  onChange={(e) => setPropertyAddress(e.target.value)}
                  placeholder="123 Main St, City, State ZIP"
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                />
              </div>
            </div>

            {submitError && (
              <div className="mt-4 p-3 rounded-md bg-rose-50 border border-rose-200 text-sm text-rose-800">
                {submitError}
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                type="submit"
                disabled={!canSubmit}
                className="px-5 py-2.5 rounded-md text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: brand }}
              >
                {submitting ? 'Booking…' : 'Confirm Booking'}
              </button>
            </div>
          </form>
        )}

        <p className="text-xs text-slate-400 text-center mt-8">
          Powered by Flowspect
        </p>
      </div>
    </div>
  );
};

export default BookingPage;
